import { z } from 'zod';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { DeepRequired } from '@work4all/utils/lib/deep-required/DeepRequired';

export const savedTableConfigZod = z
  .object({
    order: z.string().array().optional(),
    columns: z.record(
      z.string(),
      z.object({
        width: z.number().optional(),
        hidden: z.boolean().optional(),
      })
    ),
  })
  .nullable();
export type SavedTableConfig = z.infer<typeof savedTableConfigZod>;

export interface IMaskUserConfig {
  fullscreen?: boolean;
}

const ProjectZod = z
  .object({
    id: z.number(),
    number: z.string(),
    name: z.string(),
    __typename: z.string(),
  })
  .optional();

const ProjectProcessTimeTrackingZod = z.object({
  id: z.number(),
  amount: z.number().optional(),
});

const ProjectProcessZod = z
  .object({
    id: z.number(),
    process: z.string(),
    planningAmount: z.number(),
    timeTrackings: z.array(ProjectProcessTimeTrackingZod),
    __typename: z.string(),
  })
  .optional();

const BusinessPartnerZod = z.object({
  id: z.number(),
  name: z.string(),
  website: z.string(),
  __typename: z.string(),
});

const ContractZod = z.object({
  id: z.number(),
  contractNumber: z.number().optional(),
  note: z.string().optional(),
  positionList: z
    .array(
      z.object({
        id: z.number(),
        index: z.number().optional(),
        number: z.string().optional(),
        shortText: z.string().nullable().optional(),
        longtext: z.string().nullable().optional(),
        positionKind: z.nativeEnum(ErpPositionsKind).nullable().optional(),
        amount: z.number().nullable().optional(),
        unit: z.string().nullable().optional(),
        finishedDate: z.string().nullable().optional(),
        finishedByUserId: z.number().nullable().optional(),
        finishedNote: z.string().nullable().optional(),
        article: z
          .object({
            id: z.number(),
            name: z.string().optional(),
            notdeductable: z.boolean().optional(),
            isActivity: z.boolean().optional(),
          })
          .nullable()
          .optional(),
        isFinished: z.boolean().nullable().optional(),
      })
    )
    .optional(),
  __typename: z.string(),
});

const TicketZod = z.object({
  id: z.string(),
  title: z.string().optional(),
  number: z.number().optional(),
});

const CustomerZod = z.object({
  id: z.number(),
  name: z.string(),
  number: z.number().optional(),
  website: z.string().optional(),
  __typename: z.string(),
});

const PositionZod = z.object({
  id: z.number(),
  longtext: z.string().optional(),
  shortText: z.string().optional(),
  amount: z.number().optional(),
  unit: z.string().optional(),
  isFinished: z.boolean().optional(),
  number: z.string().optional(),
  __typename: z.string(),
});

const ArticleZod = z.object({
  id: z.number(),
  name: z.string().optional(),
  number: z.string().optional(),
  __typename: z.string(),
});

const TimeTrackTemplateZod = z.object({
  title: z.string(),
  subtitle1: z.string().optional(),
  subtitle2: z.string().optional(),
  subtitle3: z.string().optional(),
  subtitle4: z.string().optional(),
  toApply: z
    .object({
      project: ProjectZod.nullish(),
      projectProcess: ProjectProcessZod.nullish(),
      businessPartner: BusinessPartnerZod.nullish(),
      contract: ContractZod.nullish(),
      ticket: TicketZod.nullish(),
    })
    .nullish(),
});

export type TimeTrackTemplate = z.infer<typeof TimeTrackTemplateZod>;

export const TimeTrackTemplateListZod = TimeTrackTemplateZod.array();
export type TimeTrackTemplateList = z.infer<typeof TimeTrackTemplateListZod>;

const timeTrackingTemplateZod = z
  .object({
    id: z.number(),
    project: ProjectZod.nullish(),
    projectProcess: ProjectProcessZod.nullish(),
    customer: CustomerZod.nullish(),
    contract: ContractZod.nullish(),
    position: PositionZod.nullish(),
    ticket: TicketZod.nullish(),
    activityArticle: ArticleZod.nullish(),
    comment: z.string().optional(),
  })
  .nullish();

export const timeTrackingTemplateListZod = timeTrackingTemplateZod.array();
export type TimeTrackingTemplate = z.infer<typeof timeTrackingTemplateZod>;

const timeTrackSettingZod = z.object({
  show: z.boolean(),
  count: z.number(),
});

export const timeTrackSettingsZod = z.record(
  z.enum([
    'last',
    Entities.task,
    Entities.contract,
    Entities.project,
    Entities.appointment,
    Entities.ticket,
    Entities.timeTracking,
  ]),
  timeTrackSettingZod
);

export type TimeTrackSetting = DeepRequired<
  z.infer<typeof timeTrackSettingZod>
>;

export type TimeTrackSettings = DeepRequired<
  z.infer<typeof timeTrackSettingsZod>
>;

export interface SelectedMailboxFolder {
  mailbox: string;
  folder: string;
}
